import { doc, setDoc, getDoc, collection, addDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

// Test Firestore connection by creating a test document
export const testFirestoreConnection = async () => {
  try {
    console.log('🔄 Testing Firestore connection...');
    
    // Create a test document
    const testDoc = {
      message: 'Hello Firestore!',
      timestamp: new Date().toISOString(),
      test: true
    };
    
    // Add to test collection
    const docRef = await addDoc(collection(db, 'test'), testDoc);
    console.log('✅ Test document created with ID:', docRef.id);
    
    // Read the document back
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      console.log('✅ Test document data:', docSnap.data());
      return { success: true, id: docRef.id, data: docSnap.data() };
    } else {
      console.log('❌ No document found!');
      return { success: false, error: 'Document not found' };
    }
  } catch (error) {
    console.error('❌ Firestore connection test failed:', error);
    return { success: false, error: error.message };
  }
};

// Create a test user document to verify users collection
export const createTestUser = async () => {
  try {
    console.log('🔄 Creating test user document...');
    
    const testUser = {
      uid: 'test-user-' + Date.now(),
      email: 'test@example.com',
      firstName: 'Test',
      lastName: 'User',
      displayName: 'Test User',
      phone: '',
      dateOfBirth: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: ''
      },
      preferences: {
        emailNotifications: true,
        smsNotifications: false,
        marketingCommunications: true
      },
      profile: {
        isComplete: false,
        lastUpdated: new Date().toISOString()
      },
      createdAt: new Date().toISOString(),
      lastLogin: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    // Create document in users collection
    const userDocRef = doc(db, 'users', testUser.uid);
    await setDoc(userDocRef, testUser);
    
    console.log('✅ Test user created with UID:', testUser.uid);
    
    // Verify the document was created
    const docSnap = await getDoc(userDocRef);
    if (docSnap.exists()) {
      console.log('✅ Test user data:', docSnap.data());
      return { success: true, uid: testUser.uid, data: docSnap.data() };
    } else {
      console.log('❌ Test user document not found!');
      return { success: false, error: 'User document not found' };
    }
  } catch (error) {
    console.error('❌ Test user creation failed:', error);
    return { success: false, error: error.message };
  }
};

// Check Firestore rules and permissions
export const checkFirestoreRules = async () => {
  try {
    console.log('🔄 Checking Firestore rules...');
    
    // Try to read from a collection (this will help identify permission issues)
    const testCollection = collection(db, 'test');
    console.log('✅ Can access collections');
    
    // Try to write a simple document
    await addDoc(testCollection, { 
      test: 'rules check', 
      timestamp: new Date().toISOString() 
    });
    console.log('✅ Can write to Firestore');
    
    return { success: true, message: 'Firestore rules allow read/write operations' };
  } catch (error) {
    console.error('❌ Firestore rules check failed:', error);
    
    if (error.code === 'permission-denied') {
      return { 
        success: false, 
        error: 'Permission denied - check Firestore security rules',
        suggestion: 'Your Firestore security rules may be too restrictive. Consider updating them to allow authenticated users to read/write their own data.'
      };
    }
    
    return { success: false, error: error.message };
  }
};

// Comprehensive Firestore diagnostic
export const runFirestoreDiagnostic = async () => {
  console.log('🚀 Running comprehensive Firestore diagnostic...');
  console.log('=====================================');
  
  const results = {
    connection: await testFirestoreConnection(),
    rules: await checkFirestoreRules(),
    testUser: await createTestUser()
  };
  
  console.log('📊 Diagnostic Results:');
  console.log('Connection Test:', results.connection.success ? '✅ PASSED' : '❌ FAILED');
  console.log('Rules Check:', results.rules.success ? '✅ PASSED' : '❌ FAILED');
  console.log('User Creation:', results.testUser.success ? '✅ PASSED' : '❌ FAILED');
  
  if (results.connection.success && results.rules.success && results.testUser.success) {
    console.log('🎉 All tests passed! Firestore is working correctly.');
    console.log('💡 If you still don\'t see collections, try refreshing your Firebase console.');
  } else {
    console.log('⚠️ Some tests failed. Check the errors above.');
  }
  
  return results;
};
